import { useMemo, useState } from 'react';
import { Acc } from '../../../../components/edit-object/design/Accordion';
import { AsWritten } from '../../../../components/edit-object/design/AsWritten';
import { Field } from '../../../../components/edit-object/design/Field';
import { Btn, Input } from '../../../../components/edit-object/design/Form';
import { AutocompleteFieldMultiSelectWithFavorites } from '../../../../components/AutocompleteFieldMultiSelectWithFavorites';
import { DynamicTextFields } from '../../../../components/DynamicTextFields';
import { useSignatureData } from '../../../../hooks/useSignatureData';
import { filterTitleAndSignatureLocations } from '../../../../utils/locationFilters';
import type { ArtworkDetailsForm } from '../useArtworkDetailsForm';

interface Props {
  form: ArtworkDetailsForm;
}

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M19 13H5v-2h14z" />
  </svg>
);

export const TitleSection = ({ form }: Props) => {
  const {
    title,
    setTitle,
    titleLocation,
    setTitleLocation,
    titleAsWritten,
    setTitleAsWritten,
    alternativeTitles,
    setAlternativeTitles,
  } = form;

  const { signatureLocations, loading: locationsLoading } = useSignatureData();
  const titleLocations = useMemo(
    () => filterTitleAndSignatureLocations(signatureLocations),
    [signatureLocations],
  );

  const [showAltTitles, setShowAltTitles] = useState(alternativeTitles.length > 0);

  return (
    <Acc
      id="sec-title"
      title="Title"
      summary={title}
      emptyPlaceholder="Untitled"
      defaultExpanded
    >
      <Field label="Title of work">
        <Input
          id="title-of-work"
          big
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Sunset Over the Harbor"
        />
      </Field>

      <AsWritten heading="As written on the object">
        <AutocompleteFieldMultiSelectWithFavorites
          label="Where the title appears"
          value={titleLocation}
          options={titleLocations}
          onChange={setTitleLocation}
          loading={locationsLoading}
          vocabularyType="title_location"
          variant="design"
        />

        <Field label="Title as written">
          <Input
            value={titleAsWritten}
            onChange={(e) => setTitleAsWritten(e.target.value)}
            placeholder='e.g. "Sunset, Harbour" or in original script'
          />
        </Field>
      </AsWritten>

      <Btn
        variant="ghost"
        className="aw-toggle"
        onClick={() => setShowAltTitles((v) => !v)}
        aria-expanded={showAltTitles}
      >
        {showAltTitles ? <MinusIcon /> : <PlusIcon />}
        {showAltTitles ? 'Hide alternative titles' : 'Add alternative titles'}
      </Btn>

      {showAltTitles && (
        <div style={{ marginTop: 8 }}>
          <DynamicTextFields
            label="Alternative title"
            addButtonText="+ Add another alternative title"
            initialValues={alternativeTitles}
            onChange={setAlternativeTitles}
            variant="design"
          />
        </div>
      )}
    </Acc>
  );
};
